import { instantiateModel } from "./instantiateModel";

export class CanvasEditor {
  constructor(data = {}) {
    this.events = {};

    this.canvas = data.canvas;
    this.context = this.canvas?.getContext("2d");
    this.editorData = data?.data || {}; // Initialize with your data
    this.currentObject = undefined;
    this.highlightedObject = undefined;
    this.highlightedModifier = undefined;
    this.highlightTimeout = undefined;
    this.highlightModifierTimeout = undefined;
    this.draggable = undefined;
    this.snaplines = [];
    this.pointRadius = 15;
    this.configState = {};
    this.scale = 1;
    this.transparent = data?.transparent;

    // Initialize other properties and call necessary methods
    this.initEditor({ canvas: this.canvas, data: this.editorData });
  }

  initEditor({ canvas, data }) {
    if(!canvas && !this.canvas || !data && !this.editorData?.objects){
      return
    }

    if (this.canvas !== canvas) {
      if (this.canvas) {
        this.canvas.removeEventListener("mousedown", this.DragStart, false)
        this.canvas.removeEventListener("mousemove", this.DragMoved, false)
        this.canvas.removeEventListener("mouseup", this.DragEnded, false)
      }

      if (!(canvas instanceof HTMLCanvasElement)) {
        return;
      }

      this.canvas = canvas;
      this.context = canvas.getContext("2d");
      this.scale = 500 / this.context.canvas.height;
      this.context.scale = this.scale;

      this.canvas.addEventListener("mousedown", this.DragStart.bind(this), false)
      this.canvas.addEventListener("mousemove", this.DragMoved.bind(this), false)
      this.canvas.addEventListener("mouseup", this.DragEnded.bind(this), false)
    }

    if (this.editorData !== data ) {
      this.editorData = structuredClone(data);
    }

    if (!this.currentObject) {
      this.draggable = undefined;
      this.canvas.style.cursor = "default";
    }

    this.transparent = data?.transparent;

    this.DrawCanvas();
  }

  subscribe(eventName, callback) {
    const events = this.events[eventName] || []
    events.push(callback)
    this.events[eventName] = events;
  }

  unsubscribe(eventName, callback) {
    this.events[eventName] = (this.events[eventName] || []).filter(cb => cb !== callback);
  }

  emit(eventName, data) {
    (this.events[eventName] || []).forEach(callback => {
      callback(data)
    });
  }

  DrawCanvas() {
    const data = this.editorData;
    const ctx = this.context;

    if (!data || !ctx) {
      return ;
    }

    ctx.scale = ctx.canvas.width / data?.field_size[0];
    
    if (!this.transparent) {
      ctx.fillStyle = "rgba(28, 134, 80, 1)";
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  
      for (let i = 0; i < 12; i = i + 2) {
        let h = ctx.canvas.height / 11;
        ctx.fillStyle = "rgba(27, 130, 78, 1)";
        ctx.fillRect(0, i * h, ctx.canvas.width, h);
      }  
    } else {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }

    this.snaplines.forEach(line => {
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(255, 255, 0, 1)";
      ctx.moveTo(line[0], line[1]);
      ctx.moveTo(line[0], line[1]);
      ctx.lineTo(line[2], line[3]);
      ctx.stroke();
      }
    )

    data?.objects.forEach((item, i) => {
      const object = instantiateModel(item, ctx)

      object.active = this.currentObject === undefined || i === this.currentObject;
      object.selected = this.currentObject === i;
      object.highlighted = this.highlightedObject === i;
      object.highlightedModifier = this.highlightedModifier;
      object.context = ctx;

      object.render();
    })
  }

//--- Dragging -----------------

  DragStart(e) {
    if (this.currentObject === undefined) return;
    if (this.editorData?.objects?.[this.currentObject] === undefined) return;

    let {x,y} = this.MousePos(e);
    let object = instantiateModel(this.editorData.objects[this.currentObject], this.context);
    this.draggable = object.hitTest(x, y);
  }

  DragMoved(e) {
    const draggable = this.draggable;
    const editorData = this.editorData;
    const currentObject = this.currentObject;
    const editorContext = this.context;


    if (draggable === undefined) return;
    if (editorData?.objects?.[currentObject] === undefined) return;


    const { x, y } = this.MousePos(e);
    let object = this.editorData.objects[currentObject];

    this.canvas.style.cursor = "move";

    if (draggable?.type === "rotation") {
      let snap = e.shiftKey ? 1 : 15;

      let deltaX = x - object.origin[0];
      let deltaY = y - object.origin[1];
      let r = Math.atan2(deltaY, deltaX) * 180 / Math.PI + 86 * Math.PI;

      object.rotation = Math.round(r / snap) * snap;

      const positionEl = document.querySelector('.position-indicator');
      if (positionEl) {
        positionEl.innerHTML = Math.round(object.rotation - 90) + '&deg;'
        positionEl.classList.add('visible');

        if (x < 100) {
          positionEl.classList.add('align-right');
        } else {
          positionEl.classList.remove('align-right');
        }

      }

      this.DrawCanvas();
      return;
    }

    let newPos = [];
    let delta = [x - draggable.position[0], y - draggable.position[1]];

    if (draggable.type === "origin") {
      this.snaplines = [];

      for (let axis = 0; axis < 2; axis++) {
        let closestObject = undefined;
        let closestOffset = 4;

        for (let i = 0; i < editorData.objects.length; i++) {
          let o;

          // Hi-jack the current object to snap to center
          if (i === currentObject) {
            o = {
              origin: [Math.round(editorContext.canvas.width / 2), Math.round(editorContext.canvas.height / 2)]
            }
          } else {
            o = editorData.objects[i];
          }

          let offset = Math.abs((draggable.position[axis] + delta[axis]) - o.origin[axis]);

          if (offset < closestOffset) {
            closestObject = o;
            closestOffset = offset;
          }
        }

        if (closestObject === undefined || e.shiftKey) {
          newPos[axis] = draggable.position[axis] + delta[axis];
        } else {
          const s = this.scale;
          this.snaplines.push([object.origin[0] / s, object.origin[1] / s, closestObject.origin[0] / s, closestObject.origin[1] / s])
          newPos[axis] = closestObject.origin[axis];
        }
      }
      object.origin = newPos;

    } else if (draggable.type === 'quad') {
      newPos = [parseInt(draggable.position[0] + delta[0]), parseInt(draggable.position[1] + delta[1])]
      object.points[draggable.index][0] = newPos[0];
      object.points[draggable.index][1] = newPos[1];

    } else if (draggable.type === 'control') {
      newPos = [parseInt(draggable.position[0] + delta[0]), parseInt(draggable.position[1] + delta[1])]
      object.points[draggable.index][2] = newPos[0];
      object.points[draggable.index][3] = newPos[1];

    } else if (draggable.type === 'modifier') {
      newPos = [parseInt(draggable.position[0] + delta[0]), parseInt(draggable.position[1] + delta[1])]
      object.modifiers[draggable.index]["origin"][0] = newPos[0];
      object.modifiers[draggable.index]["origin"][1] = newPos[1];
    }

    const positionEl = document.querySelector('.position-indicator');
    if (positionEl) {
      positionEl.innerText = `${parseInt(newPos[0])}, ${parseInt(newPos[1])}`;
      positionEl.classList.add('visible');
    }

    this.DrawCanvas();
  }

   DragEnded(e) {
    this.snaplines = [];

     const positionEl = document.querySelector('.position-indicator');
     if (positionEl) {
       positionEl.classList.remove('visible');
     }

    const { x, y } = this.MousePos(e);
    if (this.currentObject === undefined) {
      for (let i = this.editorData.objects.length - 1; i >= 0; i--) {
        let object = this.editorData.objects[i];
        let dx = object.origin[0] - x;
        let dy = object.origin[1] - y;

        if ((dx * dx) + (dy * dy) < this.pointRadius * this.pointRadius) {
          this.SelectElement(i);
        }
      }
    } else {
      if (this.draggable === undefined) {
        this.AddPoint(e);
      } else {
        this.setEditorData(this.editorData)
      }
      this.draggable = undefined;
      this.canvas.style.cursor = "default";
    }
  }

  AddPoint(e) {
    if (this.currentObject === undefined || !this.editorData?.objects?.[this.currentObject]) return;

    let { x, y } = this.MousePos(e);
    let objects = [...this.editorData.objects];
    const object = objects[this.currentObject];

    if (object.points === undefined) {
      this.DeselectElement();
      return;
    }

    let prevX, prevY;

    if (object.points.length > 0) {
      prevX = object.points[object.points.length - 1][2];
      prevY = object.points[object.points.length - 1][3];
    } else {
      prevX = object.origin[0];
      prevY = object.origin[1];
    }

    let deltaX = x - prevX;
    let deltaY = y - prevY;

    if (Math.abs(deltaX + deltaY) > 0) {
      object.points.push([parseInt(prevX + deltaX / 2), parseInt(prevY + deltaY / 2), Math.round(x), Math.round(y)]);
    }

    objects[this.currentObject] = object;
    this.setEditorData({ ...this.editorData, objects})
  }

   setEditorData(data) {
    this.editorData = data;
    this.emit('editor_data_change', data);
    this.DrawCanvas();
  }

  MousePos(event) {
    const canvas = event.target; // or event.currentTarget if inside bound handler
    const rect = canvas.getBoundingClientRect(); // Get canvas position

    return {
      x: Math.round((event.clientX - rect.left) * this.scale),
      y: Math.round((event.clientY - rect.top) * this.scale)
    }
  }


//--- Manipulation -----------------
  SelectElement(index) {
    if (index === this.currentObject) {
      this.currentObject = undefined;
    } else {
      this.currentObject = index;
    }

    this.emit('selected_element', this.currentObject);

    this.DrawCanvas();
  }

  DeselectElement() {
    this.SelectElement(undefined);
  }

  SetSavedDefaults(element) {
    let defaultValue = this.configState[element.type];

    for (let attrname in defaultValue) {
      element[attrname] = defaultValue[attrname];
    }
  }

  SaveConfig(elementIndex) {
    let element = editorData.objects[elementIndex];
    if (!element.points) {
      this.configState[element.type] = {
        variation: element.variation,
        size: element.size,
        marker: element.marker
      };
    }
  }

  RemoveElement(index) {
    this.DeselectElement();
    const objects = [...this.editorData.objects]
    objects.splice(index, 1);

    this.setEditorData({ ...this.editorData, objects })
  }

  AddElement(type) {
    let d = { category: "objects", origin: [this.canvas.width / 2, this.canvas.height / 2], type }

    switch (type) {
      case "cone":
        d.variation = "red";
        break;

      case "goal":
        d.rotation = 0;
        d.size = "small";
        break;

      case "indicator":
        d.category = "indicators";
        d.marker = "A";
        break;

      case "player":
      case "player_with_ball":
        d.category = "players";
        d.variation = "green";
        d.rotation = 0;
        break;

      case "dribble":
      case "transition":
      case "pass":
      case "press":
        d.category = "movements";
        d.points = [];
        break;

      case "physical_activity_area":
        d.category = "indicators";
        d.points = [];
        break;

      case "divider":
        d.category = "indicators";
        d.points = [];
        break;

      case "hotspot":
        d.hotspot_type = "fundamentals-driving";
        break;

      default:
    }

    this.SetSavedDefaults(d);

    const objects = [...this.editorData.objects]
    let index = 0;

    if (type === "divider" || type === "physical_activity_area") {
      objects.unshift(d);
    } else {
      objects.push(d);
      index = objects?.length - 1;
    }

    this.setEditorData({ ...this.editorData, objects })
    this.SelectElement(index);
  }

  HighlightElement(index) {
    if (this.currentObject === index) return;

    this.highlightedObject = index;

    clearTimeout(this.highlightTimeout);
    this.highlightTimeout = setTimeout(() => {
      this.highlightedObject = undefined;
      this.DrawCanvas();
    }, 1000);

    this.DrawCanvas();
  }

  HighlightModifier(index) {
    this.highlightedModifier = index;
    clearTimeout(this.highlightModifierTimeout);
    this.highlightModifierTimeout = setTimeout(() => {
      this.highlightedModifier = undefined;
      this.DrawCanvas();
    }, 1000);

    this.DrawCanvas();
  }

  RemovePoint() {
    if (this.currentObject === undefined) return;

    const objects = [...this.editorData.objects]
    objects[this.currentObject]?.points?.pop();
    this.setEditorData({ ...this.editorData, objects })
  }

  AddModifier() {
    let objects = [...this.editorData.objects];
    const object = objects[this.currentObject];
    if (object.modifiers === undefined) {
      object.modifiers = [];
    }

    object.modifiers.push({ type: "turn", radius: 30, origin: [this.canvas.width / 2, this.canvas.height / 2] });

    this.setEditorData({...this.editorData, objects});
  }

  RemoveModifier(index) {
    let objects = [...this.editorData.objects];
    objects[this.currentObject]["modifiers"].splice(index, 1);
    this.setEditorData({...this.editorData, objects});
  }

  ToggleModifiers(e) {
    this.DrawCanvas();
  }

}


